import * as React from 'react';
import * as I18n from 'i18n-js';
import SanitizedRender from '@reverbdotcom/commons/dist/components/sanitized_render';
import * as BumpDiagram from '../../../images/icons/bump-marketing-icon.svg';
import HideableSlider from './hideable_slider';
import BumpBarGraphic from './bump_bar_graphic';
import BumpBidSlider, { Props as BumpBidSliderProps } from './bump_bid_slider';
import { CoreBumpListingCard } from '../../schemas/rql';
import { parseAmountCents } from '@reverbdotcom/commons/dist/money';

const exampleBumpPercent = 0.02;

function bumpChargeExplanation(
  exampleBumpPercent: string,
  listingPrice: string,
  shippingPrice: string,
  exampleSpend: string,
) {
  if (shippingPrice) {
    return (
      <SanitizedRender
        html={
          I18n.t('discovery.bump.setup.bumpChargesExplainedWithShipping', {
            exampleBumpPercent,
            listingPrice,
            shippingPrice,
            exampleSpend,
          })
        }
      />
    );
  }

  return (
    <SanitizedRender
      html={
        I18n.t('discovery.bump.setup.bumpChargesExplainedNoShipping', {
          exampleBumpPercent,
          listingPrice,
          exampleSpend,
        })
      }
    />
  );
}

function BumpSellMarketingContent1() {
  return (
    <div className="product-bump__overview-col">
      <div className="product-bump__overview-col__grid-wrap">
        <div className="product-bump__overview-col__grid-wrap__icon">
          <a href="/page/bump">
            <div className="product-bump__overview-col--bump-marketing-icon">
              <img src={BumpDiagram} alt={I18n.t('discovery.bump.setup.explanation.bumpIcon')} className="d-block width-100" />
            </div>
          </a>
        </div>
        <div className="product-bump__overview-col__marketing-wrap">
          <h3 className="product-bump__overview-col__heading">
            {I18n.t('discovery.bump.setup.bumpHelpsSell')}
          </h3>
          <p className="product-bump__overview-col__subheading">
            <SanitizedRender
              html={
                I18n.t('discovery.bump.setup.bumpHelpsSellSubheading', {
                  linkOpen: '<a href="/page/bump" target="_blank">',
                  linkClose: '</a>',
                })
              }
            />
          </p>
          <ol className="product-bump__overview-col__benefits-list">
            <li className="product-bump__overview-col__benefits-list__item">
              <span>{I18n.t('discovery.bump.setup.explanation.one')}</span>
              {I18n.t('discovery.bump.setup.explanation.stepOne')}
            </li>
            <li className="product-bump__overview-col__benefits-list__item">
              <span>{I18n.t('discovery.bump.setup.explanation.two')}</span>
              {I18n.t('discovery.bump.setup.explanation.stepTwo')}
            </li>
            <li className="product-bump__overview-col__benefits-list__item">
              <span>{I18n.t('discovery.bump.setup.explanation.three')}</span>
              {I18n.t('discovery.bump.setup.explanation.stepThree')}
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

interface BumpGraphicProps {
  isNewCondition: boolean;
}

function BumpGraphic(props: BumpGraphicProps) {
  return (
    <div>
      <h4 className="product-bump__slider-col__label">
        {I18n.t('discovery.bump.setup.pickBumpTier')}
      </h4>
      <div className="product-bump__views-scale">
        <div className="product-bump__scale-img">
          <div className="product-bump__scale-img__inner">
            <BumpBarGraphic
              highlightStartPercentage={50}
              highlightEndPercentage={80}
            />
          </div>
        </div>
        <div className="product-bump__views-scale__more">{I18n.t('discovery.bump.setup.moreViews')}</div>
        <div className={
          `product-bump__views-scale__most-popular
              ${props.isNewCondition ?
      ' product-bump__views-scale__most-popular--new' :
      ' product-bump__views-scale__most-popular--used'
    }
            `
        }
        >
          {I18n.t('discovery.bump.setup.recommendedTiers')}
        </div>
        <div className="product-bump__views-scale__much-more">{I18n.t('discovery.bump.setup.manyMoreViews')}</div>
      </div>
    </div>
  );
}

interface CalculateExampleSpendProps {
  listingPriceAmountCents: number;
  shippingPriceAmountCents: number;
  currency: string;
}

function calculateExampleSpend(props: CalculateExampleSpendProps) {
  const spendAmountCents = exampleBumpPercent * (props.listingPriceAmountCents + props.shippingPriceAmountCents);

  return parseAmountCents(spendAmountCents, props.currency).display;
}

function exampleBumpPercentDisplay(exampleBumpPercent): string {
  return (exampleBumpPercent * 100).toString();
}

interface BumpSetupProps {
  bumpBidSliderProps: BumpBidSliderProps;
  listingPrice: CoreBumpListingCard.SellerPrice;
  shippingPrice: CoreBumpListingCard.OriginalRate;
  isNewCondition?: boolean;
  showMobileSlider?: boolean;
  sliderToggle?: Function;
}

function BumpSetup(props: BumpSetupProps) {
  const exampleSpend = calculateExampleSpend({
    listingPriceAmountCents: props.listingPrice.amountCents,
    shippingPriceAmountCents: props.shippingPrice.amountCents,
    currency: props.listingPrice.currency,
  });

  return (
    <div className="product-bump__wrapper">
      <BumpSellMarketingContent1 />
      <div className="product-bump__slider-col">
        <HideableSlider
          label={I18n.t('discovery.bump.setup.selectBumpTier')}
          sliderToggle={props.sliderToggle}
          showMobileSlider={props.showMobileSlider}
        >
          <BumpGraphic isNewCondition={props.isNewCondition} />
          <BumpBidSlider {...props.bumpBidSliderProps} />
          <h4 className="bump-charges__heading">{I18n.t('discovery.bump.setup.example')}</h4>
          <p className="bump-charges__description">
            {bumpChargeExplanation(exampleBumpPercentDisplay(exampleBumpPercent), props.listingPrice.display, props.shippingPrice.display, exampleSpend)}
          </p>
        </HideableSlider>
      </div>
    </div>
  );
}

export default BumpSetup;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/bump/bump_setup.tsx